import type { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { signIn, getCsrfToken } from "next-auth/react";
import { getServerSession } from "@calcom/features/auth/lib/getServerSession";
import { useState } from "react";
import { Zap, AlertCircle, CheckCircle, Mail, Lock, Eye, EyeOff } from "lucide-react";

type Tab = "password" | "magic";

export default function RwgBridgeLogin({ csrfToken }: { csrfToken?: string }) {
  const router = useRouter();
  const [tab, setTab] = useState<Tab>("password");

  // Password form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Magic link form
  const [magicEmail, setMagicEmail] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
      callbackUrl: "/rwg-bridge/dashboard",
    });

    if (result?.error) {
      setError("Invalid email or password.");
      setLoading(false);
      return;
    }

    const authCheck = await fetch("/api/rwg-bridge/auth-check");
    const data = await authCheck.json();
    router.push(data.onboardingComplete ? "/rwg-bridge/dashboard" : "/rwg-bridge/onboarding");
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLoading(true);
    setMagicError(null);

    const res = await fetch("/api/rwg-bridge/auth/magic-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: magicEmail }),
    });

    setMagicLoading(false);
    if (res.ok) {
      setMagicSent(true);
    } else {
      setMagicError("Failed to send link. Please try again.");
    }
  };

  const tabClass = (t: Tab) =>
    `flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
      tab === t ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-800"
    }`;

  return (
    <>
      <Head>
        <title>Sign in · RWG Bridge</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-200">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-900">RWG Bridge</h1>
              <p className="mt-1 text-sm text-gray-500">Sign in to manage your Google presence</p>
            </div>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            {/* Tab switcher */}
            <div className="mb-6 flex rounded-xl bg-gray-100 p-1">
              <button className={tabClass("password")} onClick={() => setTab("password")}>
                <Lock className="mb-0.5 mr-1.5 inline-block h-3.5 w-3.5" />
                Password
              </button>
              <button className={tabClass("magic")} onClick={() => setTab("magic")}>
                <Mail className="mb-0.5 mr-1.5 inline-block h-3.5 w-3.5" />
                Magic link
              </button>
            </div>

            {/* ── PASSWORD TAB ── */}
            {tab === "password" && (
              <>
                {error && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}

                <form onSubmit={handlePasswordSignIn} className="space-y-4">
                  <input type="hidden" name="csrfToken" value={csrfToken} />

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Email address</label>
                    <input
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Password</label>
                      <a href="/rwg-bridge/forgot-password" className="text-xs text-blue-600 hover:underline">
                        Forgot password?
                      </a>
                    </div>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        required
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 pr-10 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 transition-colors">
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </form>
              </>
            )}

            {/* ── MAGIC LINK TAB ── */}
            {tab === "magic" && (
              <>
                {magicSent ? (
                  <div className="flex flex-col items-center gap-4 py-2 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Check your inbox</p>
                      <p className="mt-1 text-sm text-gray-500">
                        We sent a sign-in link to <strong>{magicEmail}</strong>. It expires in 1 hour.
                      </p>
                    </div>
                    <button
                      onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                      className="text-sm text-blue-600 hover:underline">
                      Use a different email
                    </button>
                  </div>
                ) : (
                  <>
                    {magicError && (
                      <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        {magicError}
                      </div>
                    )}

                    <p className="mb-4 text-sm text-gray-500">
                      Enter your email and we&apos;ll send a one-click sign-in link — no password needed.
                    </p>

                    <form onSubmit={handleMagicLink} className="space-y-4">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">Email address</label>
                        <input
                          type="email"
                          required
                          autoFocus
                          value={magicEmail}
                          onChange={(e) => setMagicEmail(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          placeholder="you@example.com"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={magicLoading}
                        className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors">
                        {magicLoading ? "Sending…" : "Send magic link"}
                      </button>
                    </form>
                  </>
                )}
              </>
            )}

            <p className="mt-5 text-center text-xs text-gray-400">
              Don&apos;t have an account?{" "}
              <a href="/rwg-bridge/signup" className="font-medium text-blue-600 hover:underline">
                Create account
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ req, res }) => {
  const session = await getServerSession({ req: req as Parameters<typeof getServerSession>[0]["req"], res });
  if (session?.user?.id) {
    return { redirect: { destination: "/rwg-bridge/dashboard", permanent: false } };
  }
  const csrfToken = await getCsrfToken({ req });
  return { props: { csrfToken: csrfToken ?? null } };
};
